/* eslint-disable max-lines */
import * as Yup from 'yup';

import {
    CategoryAssignment,
    CustomDrink,
    CustomDrinkRecipeItem,
    PourAvailability,
    StandardDrink,
    StandardDrinkRecipeItem,
    StandardDrinkSubstitution,
} from '@barboxx/models/types';
import {
    DRINK_RECIPE_ITEM_TYPE,
    DrinkRecipeItemType,
    FLAVOR_TYPES,
    MEDIUM_TEXT,
    MENU_ITEM_TYPES,
    MenuItemType,
    MINIMUM_TEXT,
    POUR_AVAILABILITY_ENTITY_TYPE,
    PourAvailabilityEntityType,
    TINY_TEXT,
} from '@barboxx/types';
import { enumToList, transformEmptyToNull } from '@barboxx/utils';

export const CUSTOM_DRINK_SCHEMA = Yup.object<CustomDrink.Base>().shape({
    id: Yup.number().required(),
    businessId: Yup.number().required(),
    name: Yup.string()
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(TINY_TEXT, 'Too Long!')
        .required(),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    preparationInstructions: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    image: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
    isAlcoholic: Yup.boolean().default(true),
    isShot: Yup.boolean().default(false),
    basePrice: Yup.number().positive().required(),
    upCharge: Yup.number().positive().default(1).required(),
});
export const CREATE_CUSTOM_DRINK_SCHEMA = CUSTOM_DRINK_SCHEMA.omit(['id']);

export const STANDARD_DRINK_SCHEMA = Yup.object<StandardDrink.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string().required('Required'),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    preparationInstructions: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    image: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
    isAlcoholic: Yup.boolean().default(true),
    isShot: Yup.boolean().default(false),
});
export const CREATE_STANDARD_DRINK_SCHEMA = STANDARD_DRINK_SCHEMA.omit(['id']);

export const STANDARD_DRINK_RECIPE_ITEM_SCHEMA =
    Yup.object<StandardDrinkRecipeItem.Base>().shape({
        id: Yup.number().required(),
        type: Yup.string()
            .oneOf<DrinkRecipeItemType>(enumToList(DRINK_RECIPE_ITEM_TYPE))
            .required(),
        standardDrinkId: Yup.number().required(),
        quantity: Yup.number().nullable().default(null),
        unit: Yup.string()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null)
            .min(MINIMUM_TEXT, 'Too Short!')
            .max(TINY_TEXT, 'Too Long!'),
        isOptional: Yup.boolean().default(false),
        noSubstitution: Yup.boolean().default(false),
        productId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) => type === DRINK_RECIPE_ITEM_TYPE.Product,
                then: schema => schema.required(),
            }),
        productTypeId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.ProductType,
                then: schema => schema.required(),
            }),
        productSubtypeId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.ProductSubtype,
                then: schema => schema.required(),
            }),
        flavor: Yup.string()
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.ProductType ||
                    type === DRINK_RECIPE_ITEM_TYPE.ProductSubtype,
                then: schema => schema.oneOf(enumToList(FLAVOR_TYPES)),
            })
            .nullable()
            .defined(),
        priority: Yup.number().default(0).required(),
        ingredientId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.Ingredient,
                then: schema => schema.required(),
            }),
    });
export const CREATE_STANDARD_DRINK_RECIPE_ITEM_SCHEMA =
    STANDARD_DRINK_RECIPE_ITEM_SCHEMA.omit(['id']);

export const CATEGORY_ASSIGNMENT_SCHEMA =
    Yup.object<CategoryAssignment.Base>().shape({
        id: Yup.number().required(),
        type: Yup.string()
            .oneOf<MenuItemType>(enumToList(MENU_ITEM_TYPES))
            .required(),
        categoryId: Yup.number().required(),
        displayOrder: Yup.number().nullable().default(null),
        isActive: Yup.boolean().default(true),
        standardDrinkId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) => type === MENU_ITEM_TYPES.StandardDrink,
                then: schema => schema.required(),
            }),
        customDrinkId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) => type === MENU_ITEM_TYPES.CustomDrink,
                then: schema => schema.required(),
            }),
        productId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) => type === MENU_ITEM_TYPES.Product,
                then: schema => schema.required(),
            }),
    });
export const CREATE_CATEGORY_ASSIGNMENT_SCHEMA =
    CATEGORY_ASSIGNMENT_SCHEMA.omit(['id']);

export const STANDARD_DRINK_SUBSTITUTION_SCHEMA =
    Yup.object<StandardDrinkSubstitution.Base>().shape({
        id: Yup.number().required(),
        drinkId: Yup.number().required(),
        substitutionId: Yup.number().required(),
        priority: Yup.number().default(0).required(),
    });
export const CREATE_STANDARD_DRINK_SUBSTITUTION_SCHEMA =
    STANDARD_DRINK_SUBSTITUTION_SCHEMA.omit(['id']);

export const POUR_AVAILABILITY_SCHEMA =
    Yup.object<PourAvailability.Base>().shape({
        id: Yup.number().required(),
        pourFormatId: Yup.number().required(),
        entityType: Yup.string()
            .oneOf<PourAvailabilityEntityType>(
                enumToList(POUR_AVAILABILITY_ENTITY_TYPE)
            )
            .required(),
        categoryId: Yup.number()
            .nullable()
            .default(null)
            .when('entityType', {
                is: (type: string) =>
                    type === POUR_AVAILABILITY_ENTITY_TYPE.Category,
                then: schema => schema.required(),
            }),
        standardDrinkId: Yup.number()
            .nullable()
            .default(null)
            .when(['entityType'], {
                is: (type: string) =>
                    type === POUR_AVAILABILITY_ENTITY_TYPE.Drink,
                then: schema => schema.required(),
            })
            .when('entityType', {
                is: (type: PourAvailabilityEntityType) => type === 'Drink',
                then: schema => schema.required(),
                otherwise: schema => schema.nullable().default(null),
            }),
        productLineId: Yup.number()
            .nullable()
            .default(null)
            .when('entityType', {
                is: (type: string) =>
                    type === POUR_AVAILABILITY_ENTITY_TYPE.ProductLine,
                then: schema => schema.required(),
            }),
        productId: Yup.number()
            .nullable()
            .default(null)
            .when('entityType', {
                is: (type: string) =>
                    type === POUR_AVAILABILITY_ENTITY_TYPE.Product,
                then: schema => schema.required(),
            }),
        productTypeId: Yup.number()
            .nullable()
            .default(null)
            .when('entityType', {
                is: (type: string) =>
                    type === POUR_AVAILABILITY_ENTITY_TYPE.ProductType,
                then: schema => schema.required(),
            }),
        productSubtypeId: Yup.number()
            .nullable()
            .default(null)
            .when('entityType', {
                is: (type: string) =>
                    type === POUR_AVAILABILITY_ENTITY_TYPE.ProductSubtype,
                then: schema => schema.required(),
            }),
        isDefault: Yup.boolean().default(false),
        displayOrder: Yup.number().default(0).required(),
    });
// .test(
//     'customDrink-or-standardDrink',
//     'Either customDrinkId or standardDrinkId must be defined, but not both.',
//     function ({ standardDrinkId, entityType }) {
//         if (
//             (entityType === POUR_AVAILABILITY_ENTITY_TYPE.Drink &&
//                 standardDrinkId) ||
//             (entityType === POUR_AVAILABILITY_ENTITY_TYPE.Drink &&
//                 !standardDrinkId)
//         ) {
//             return this.createError({
//                 path: 'standardDrinkId',
//                 message: `Either customDrinkId or standardDrinkId must be defined, but not both.`,
//             });
//         }
//         return true;
//     }
// );
export const CREATE_POUR_AVAILABILITY_SCHEMA = POUR_AVAILABILITY_SCHEMA.omit([
    'id',
]);

export const CUSTOM_DRINK_RECIPE_ITEM_SCHEMA =
    Yup.object<CustomDrinkRecipeItem.Base>().shape({
        id: Yup.number().required(),
        type: Yup.string()
            .oneOf<DrinkRecipeItemType>(enumToList(DRINK_RECIPE_ITEM_TYPE))
            .required(),
        customDrinkId: Yup.number().required(),
        quantity: Yup.number().nullable().default(null),
        unit: Yup.string()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null)
            .min(MINIMUM_TEXT, 'Too Short!')
            .max(TINY_TEXT, 'Too Long!'),
        isOptional: Yup.boolean().default(false),
        productId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) => type === DRINK_RECIPE_ITEM_TYPE.Product,
                then: schema => schema.required(),
            }),
        productTypeId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.ProductType,
                then: schema => schema.required(),
            }),
        productSubtypeId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.ProductSubtype,
                then: schema => schema.required(),
            }),
        ingredientId: Yup.number()
            .nullable()
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.Ingredient,
                then: schema => schema.required(),
            }),
        flavor: Yup.string()
            // .oneOf(enumToList(FLAVOR_TYPES))
            .default(null)
            .when('type', {
                is: (type: string) =>
                    type === DRINK_RECIPE_ITEM_TYPE.ProductType ||
                    type === DRINK_RECIPE_ITEM_TYPE.ProductSubtype,
                then: schema => schema.oneOf(enumToList(FLAVOR_TYPES)),
            })
            .nullable()
            .defined(),
        priority: Yup.number().default(0).required(),
    });
export const CREATE_CUSTOM_DRINK_RECIPE_ITEM_SCHEMA =
    CUSTOM_DRINK_RECIPE_ITEM_SCHEMA.omit(['id']);
/* eslint-enable max-lines */
